import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownRightIcon, ArrowRightIcon, ArrowUpRightIcon, RefreshCwIcon, SendIcon } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorNotice,
  LoadingState,
  Modal,
  PageHeading,
  Panel,
  Pill,
  SegmentedControl,
  SelectInput,
  StatTile,
  TextArea,
  TextInput,
  TrendChart,
  type PillTone,
  type TrendPoint,
} from '@bcecloud/portal-ui';
import { errorMessage, sendJson } from '@/lib/api';
import {
  GRAIN_LABELS,
  REPORT_GRAINS,
  readReportByPeriod,
  readReportHistory,
  sendReport,
  type AnalysisSeverity,
  type AnalysisTrend,
  type Kpi,
  type ReportGrain,
  type ReportMetrics,
  type ReportTable,
  type Series,
  type StoredReport,
} from '@/lib/reports';

const SEVERITY_TONE: Readonly<Record<AnalysisSeverity, PillTone>> = {
  good: 'success',
  watch: 'warning',
  bad: 'danger',
};
const VERDICT_TONE: Readonly<Record<string, PillTone>> = {
  strong: 'success',
  steady: 'info',
  soft: 'warning',
  concern: 'danger',
};

/**
 * The numbers the owner always wants to see first — the run-rate and its levers,
 * plus the two headline volumes. Rendered as a fixed strip above the findings.
 * Keys must match the KPI keys the snapshot emits (unit economics + bookings +
 * leads); any missing key is skipped.
 */
const NORTH_STAR_KEYS = [
  'runRateAnnual',
  'runRate3mo',
  'leadsPerWeek',
  'closeRate',
  'avgBookingValue',
  'bookings',
  'leads',
] as const;

const compactMoney = (value: number): string =>
  new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1, style: 'currency', currency: 'USD' }).format(value);

/** StatTile props from a KPI: honor the KPI's own good/bad even when it fell. */
function statProps(kpi: Kpi): { label: string; value: string; changePct: number | null; hint: string; downIsGood: boolean } {
  const delta = kpi.delta ?? null;
  const changePct = delta?.pct ?? null;
  // downIsGood flips StatTile's coloring so "down but good" reads green.
  const downIsGood = delta ? ((delta.pct ?? 0) >= 0) !== delta.good : false;
  return {
    label: kpi.label,
    value: kpi.display,
    changePct,
    hint: kpi.sub ?? (delta ? delta.label : ''),
    downIsGood,
  };
}

function toPoints(series: Series): TrendPoint[] {
  return series.periods.map((period, i) => ({ key: period, label: period, value: series.values[i] ?? 0 }));
}

/** Flatten every section's KPIs into one key→KPI index so findings can resolve their metricRefs. */
function indexKpis(metrics: ReportMetrics): ReadonlyMap<string, Kpi> {
  const map = new Map<string, Kpi>();
  for (const section of metrics.sections) {
    for (const kpi of section.kpis) if (!map.has(kpi.key)) map.set(kpi.key, kpi);
  }
  return map;
}

function findSeries(metrics: ReportMetrics, sectionKey: string, seriesKey: string): Series | null {
  const section = metrics.sections.find((s) => s.key === sectionKey);
  return section?.series?.find((s) => s.key === seriesKey) ?? null;
}

function sectionTables(metrics: ReportMetrics, sectionKey: string): readonly ReportTable[] {
  return metrics.sections.find((s) => s.key === sectionKey)?.tables ?? [];
}

/** A compact report table in a Panel — the design-system table styling. */
function TablePanel({ table }: { readonly table: ReportTable }): React.ReactElement {
  return (
    <Panel className="p-5">
      <h3 className="mb-2 text-portal-card-title font-bold text-portal-ink">{table.title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-portal-small">
          <thead>
            <tr>
              {table.columns.map((col, i) => (
                <th
                  key={col}
                  className={`border-b border-portal-line p-3 text-portal-micro font-bold uppercase tracking-wide text-portal-muted ${i === 0 ? 'text-left' : 'text-right'}`}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.length === 0 ? (
              <tr>
                <td colSpan={table.columns.length} className="p-3 text-portal-muted">
                  No data
                </td>
              </tr>
            ) : (
              table.rows.map((row, r) => (
                <tr key={r} className="border-t border-portal-line align-middle">
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      className={`p-3 ${c === 0 ? 'font-bold text-portal-ink' : 'whitespace-nowrap text-right text-portal-muted'}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/** The always-on strip of headline numbers, resolved from the KPI index. */
function NorthStarStrip({ kpis }: { readonly kpis: ReadonlyMap<string, Kpi> }): React.ReactElement | null {
  const tiles = NORTH_STAR_KEYS.map((key) => kpis.get(key)).filter((k): k is Kpi => k !== undefined);
  if (tiles.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      {tiles.map((kpi) => (
        <StatTile key={kpi.key} {...statProps(kpi)} />
      ))}
    </div>
  );
}

/** A finding's cited metrics, rendered inline beside it so the evidence is right there. */
function Evidence({ refs, kpis }: { readonly refs: readonly string[]; readonly kpis: ReadonlyMap<string, Kpi> }): React.ReactElement | null {
  const cited = refs.map((key) => kpis.get(key)).filter((k): k is Kpi => k !== undefined);
  if (cited.length === 0) return null;
  return (
    <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-3">
      {cited.map((kpi) => (
        <StatTile key={kpi.key} {...statProps(kpi)} />
      ))}
    </div>
  );
}

const TREND_ICON: Readonly<Record<AnalysisTrend['direction'], typeof ArrowUpRightIcon>> = {
  up: ArrowUpRightIcon,
  down: ArrowDownRightIcon,
  flat: ArrowRightIcon,
};

function ReportBody({ report }: { readonly report: StoredReport }): React.ReactElement {
  const { metrics, analysis } = report;
  const kpis = indexKpis(metrics);
  const runRateTrend = findSeries(metrics, 'unitEconomics', 'runRateTrend');
  const ueTables = sectionTables(metrics, 'unitEconomics');

  return (
    <div className="space-y-6">
      {/* North-star: the numbers you always look at. */}
      <section>
        <h2 className="mb-3 text-portal-section font-bold text-portal-ink">The numbers</h2>
        <NorthStarStrip kpis={kpis} />
        {runRateTrend ? (
          <Panel className="mt-4 p-5">
            <h3 className="text-portal-card-title font-bold text-portal-ink">{runRateTrend.label}</h3>
            <TrendChart
              points={toPoints(runRateTrend)}
              title={runRateTrend.label}
              unit="USD"
              variant="bar"
              height={220}
              formatValue={compactMoney}
            />
          </Panel>
        ) : null}
        {ueTables.length > 0 ? (
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            {ueTables.map((table) => (
              <TablePanel key={table.key} table={table} />
            ))}
          </div>
        ) : null}
      </section>

      {/* The analyst's read, findings-first. */}
      {analysis ? (
        <>
          <Panel className="p-5">
            <div className="mb-1 flex items-center gap-2">
              <Pill tone={VERDICT_TONE[analysis.overallVerdict] ?? 'neutral'}>{analysis.overallVerdict}</Pill>
              <span className="text-portal-micro text-portal-muted">
                {metrics.period.label} · generated {new Date(analysis.generatedAt).toLocaleString()}
              </span>
            </div>
            <p className="text-portal-section font-bold text-portal-ink">{analysis.headline}</p>
          </Panel>

          {analysis.callouts.length > 0 ? (
            <section>
              <h2 className="mb-3 text-portal-section font-bold text-portal-ink">What to look at</h2>
              <ul className="space-y-4">
                {analysis.callouts.map((c, i) => (
                  <li key={i}>
                    <Panel className="p-5">
                      <div className="flex items-center gap-2">
                        <Pill tone={SEVERITY_TONE[c.severity]}>{c.severity}</Pill>
                        <span className="text-portal-card-title font-bold text-portal-ink">{c.title}</span>
                      </div>
                      <p className="mt-1 text-portal-small text-portal-muted">{c.detail}</p>
                      {c.recommendedAction ? (
                        <p className="mt-2 text-portal-small font-bold text-portal-ink">→ {c.recommendedAction}</p>
                      ) : null}
                      <Evidence refs={c.metricRefs} kpis={kpis} />
                    </Panel>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {analysis.anomalies.length > 0 ? (
            <section>
              <h2 className="mb-3 text-portal-section font-bold text-portal-ink">Anomalies</h2>
              <ul className="space-y-3">
                {analysis.anomalies.map((a, i) => (
                  <li key={i}>
                    <Panel className="p-5">
                      <div className="flex items-center gap-2">
                        <Pill tone={SEVERITY_TONE[a.severity]}>{a.severity}</Pill>
                        <span className="text-portal-card-title font-bold text-portal-ink">{a.title}</span>
                      </div>
                      <p className="mt-1 text-portal-small text-portal-muted">{a.evidence}</p>
                      {a.likelyCause ? (
                        <p className="mt-1 text-portal-micro text-portal-muted">
                          <span className="font-bold text-portal-ink">Likely cause: </span>
                          {a.likelyCause}
                        </p>
                      ) : null}
                      {a.suggestedInvestigation ? (
                        <p className="mt-1 text-portal-micro text-portal-ink">→ {a.suggestedInvestigation}</p>
                      ) : null}
                    </Panel>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {analysis.trends.length > 0 ? (
            <section>
              <h2 className="mb-3 text-portal-section font-bold text-portal-ink">Trends</h2>
              <ul className="grid gap-3 lg:grid-cols-2">
                {analysis.trends.map((t, i) => {
                  const Icon = TREND_ICON[t.direction];
                  return (
                    <li key={i}>
                      <Panel className="p-5">
                        <div className="flex items-center gap-2">
                          <Icon className="size-4 text-portal-muted" aria-hidden="true" />
                          <span className="text-portal-card-title font-bold text-portal-ink">{t.title}</span>
                          <span className="text-portal-micro text-portal-muted">{t.momentum}</span>
                        </div>
                        <p className="mt-1 text-portal-small text-portal-muted">{t.detail}</p>
                      </Panel>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          {analysis.sectionSummaries.length > 0 ? (
            <section>
              <h2 className="mb-3 text-portal-section font-bold text-portal-ink">Period summary</h2>
              <Panel className="p-5">
                <dl className="space-y-2">
                  {analysis.sectionSummaries.map((s) => (
                    <div key={s.section} className="text-portal-small">
                      <dt className="font-bold capitalize text-portal-ink">{s.section}</dt>
                      <dd className="text-portal-muted">{s.summary}</dd>
                    </div>
                  ))}
                </dl>
              </Panel>
            </section>
          ) : null}
        </>
      ) : (
        <Panel>
          <EmptyState
            title="No AI analysis yet"
            message="The headline numbers are above. Press Rebuild to run the analyst over them."
          />
        </Panel>
      )}

      <div className="rounded-portal-button bg-portal-bg p-3 text-portal-micro text-portal-muted">
        <span className="font-bold text-portal-ink">How fresh is this? </span>
        {metrics.freshness.join(' · ')}
      </div>
    </div>
  );
}

/** The send-to-stakeholders dialog: enter emails + an optional note → PDF email. */
function SendDialog({
  open,
  grain,
  period,
  periodLabel,
  onClose,
}: {
  readonly open: boolean;
  readonly grain: ReportGrain;
  readonly period: string;
  readonly periodLabel: string;
  readonly onClose: () => void;
}): React.ReactElement {
  const [recipients, setRecipients] = useState('');
  const [note, setNote] = useState('');

  const send = useMutation({
    mutationFn: () =>
      sendReport({
        grain,
        ...(period ? { period } : {}),
        recipients: recipients
          .split(/[,\s]+/)
          .map((email) => email.trim())
          .filter((email) => email.length > 0),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
  });

  const emails = recipients
    .split(/[,\s]+/)
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

  function close(): void {
    send.reset();
    setRecipients('');
    setNote('');
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Send to stakeholders"
      subtitle={`${GRAIN_LABELS[grain]} report — ${periodLabel}, as a PDF attachment`}
      actions={
        <>
          <Button variant="secondary" onClick={close} disabled={send.isPending}>
            {send.isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {send.isSuccess ? null : (
            <Button onClick={() => send.mutate()} disabled={send.isPending || emails.length === 0}>
              <SendIcon className="size-4" aria-hidden="true" />
              {send.isPending ? 'Sending…' : `Send${emails.length > 0 ? ` to ${emails.length}` : ''}`}
            </Button>
          )}
        </>
      }
    >
      {send.isSuccess ? (
        <p className="text-portal-small text-portal-ink">
          Sent to {send.data.recipients} {send.data.recipients === 1 ? 'recipient' : 'recipients'}.
        </p>
      ) : (
        <div className="space-y-3">
          <div>
            <label htmlFor="send-recipients" className="mb-1 block text-portal-small font-bold text-portal-ink">
              Recipients
            </label>
            <TextInput
              id="send-recipients"
              value={recipients}
              onChange={(event) => setRecipients(event.target.value)}
              placeholder="email@company.com, another@company.com"
            />
            <p className="mt-1 text-portal-micro text-portal-muted">Separate multiple addresses with a comma or space.</p>
          </div>
          <div>
            <label htmlFor="send-note" className="mb-1 block text-portal-small font-bold text-portal-ink">
              Note <span className="font-normal text-portal-muted">(optional)</span>
            </label>
            <TextArea
              id="send-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              placeholder="A short line to open the email."
            />
          </div>
          {send.isError ? <ErrorNotice title="Send failed" message={errorMessage(send.error)} /> : null}
        </div>
      )}
    </Modal>
  );
}

/**
 * The Company Analyst dashboard: a fixed north-star strip of the numbers that
 * always matter, then the AI analyst's findings (callouts, anomalies, trends)
 * with each finding's cited metrics inline. Deep per-domain metrics live in the
 * dedicated Analytics tabs — this page curates rather than dumps. A period
 * selector loads any archived report, and stakeholders can be emailed the
 * report as a PDF. Admin-only (view_analytics), enforced by the API and the
 * route guard.
 */
export function ReportsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [grain, setGrainState] = useState<ReportGrain>('week');
  const [period, setPeriod] = useState('');
  const [sendOpen, setSendOpen] = useState(false);

  function setGrain(next: ReportGrain): void {
    setGrainState(next);
    setPeriod(''); // A new grain's periods differ; fall back to its latest.
  }

  const history = useQuery({
    queryKey: ['reports-history', grain],
    queryFn: ({ signal }) => readReportHistory(grain, signal),
  });

  const report = useQuery({
    queryKey: ['reports-by-period', grain, period],
    queryFn: ({ signal }) => readReportByPeriod(grain, period || null, signal),
    placeholderData: keepPreviousData,
  });

  const rebuild = useMutation({
    mutationFn: () => sendJson('post', `/reports/build?grain=${grain}&analyze=true`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports-by-period', grain] });
      queryClient.invalidateQueries({ queryKey: ['reports-history', grain] });
    },
  });

  const selected = report.data ?? null;
  const periods = history.data?.periods ?? [];
  const periodLabel = selected?.metrics.period.label ?? periods[0]?.label ?? '';

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <PageHeading title="Reports" subtitle="How the business is doing — analyzed." />
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedControl<ReportGrain>
            label="Cadence"
            value={grain}
            onChange={setGrain}
            options={REPORT_GRAINS.map((key) => ({ key, label: GRAIN_LABELS[key] }))}
          />
          {periods.length > 0 ? (
            <div>
              <label htmlFor="report-period" className="sr-only">
                Report period
              </label>
              <SelectInput id="report-period" value={period} onChange={(event) => setPeriod(event.target.value)}>
                <option value="">Latest ({periods[0]?.label})</option>
                {periods.map((entry) => (
                  <option key={entry.periodStart} value={entry.periodStart}>
                    {entry.label}
                  </option>
                ))}
              </SelectInput>
            </div>
          ) : null}
          <Button
            variant="secondary"
            onClick={() => setSendOpen(true)}
            disabled={selected === null}
            aria-label="Email this report to stakeholders as a PDF"
          >
            <SendIcon className="size-4" aria-hidden="true" />
            Send
          </Button>
          <Button
            variant="secondary"
            onClick={() => rebuild.mutate()}
            disabled={rebuild.isPending}
            aria-label="Rebuild this report now, including the AI analysis"
          >
            <RefreshCwIcon className={rebuild.isPending ? 'size-4 animate-spin' : 'size-4'} aria-hidden="true" />
            {rebuild.isPending ? 'Rebuilding…' : 'Rebuild'}
          </Button>
        </div>
      </div>

      {rebuild.isError ? (
        <div className="mb-4">
          <ErrorNotice title="Rebuild failed" message={errorMessage(rebuild.error)} />
        </div>
      ) : null}

      {report.isError ? (
        <ErrorNotice title="Report unavailable" message={errorMessage(report.error)} />
      ) : report.isPending ? (
        <LoadingState label="Loading the report…" />
      ) : selected === null ? (
        <Panel>
          <EmptyState
            title={`No ${GRAIN_LABELS[grain].toLowerCase()} report yet`}
            message="Nothing has been built for this period. Press Rebuild to generate it now."
          />
        </Panel>
      ) : (
        <ReportBody report={selected} />
      )}

      <SendDialog
        open={sendOpen}
        grain={grain}
        period={period}
        periodLabel={periodLabel}
        onClose={() => setSendOpen(false)}
      />
    </div>
  );
}
